import { tt } from '@agos/shared'

/**
 * Who may write which half of `/loyalty-program`.
 *
 * One page, **two** write rules, because it drives two controllers:
 *
 * | Tab | Endpoints | Read | Write |
 * |---|---|---|---|
 * | Nastavenia | `PUT /settings/loyalty`, `POST`/`PATCH`/`DELETE /loyalty/levels...`, `POST /customers/backfill-levels` | see below | **`loyalty_program_edit`** |
 * | Kampane | `PATCH /campaigns/{type}`, `POST /campaigns/{type}/run`, `PATCH /campaigns/send-cap` | `main_admin`, `admin` | `main_admin`, `admin` |
 *
 * **The settings tab asked `systemRole === 'main_admin'` until §9.758** (BACKEND.md §10A.413, owner, folded into
 * O119), because every one of its writes was `@RequireAuth("main_admin")`. They ask `loyalty_program_edit` now - the
 * write key alone, never paired with `loyalty_program`, which a line role may hold - and the programme and referral
 * fields this tab sends are that right's on `PUT /settings/loyalty`, which asks by field. So the tab asks the right,
 * through `hasPermission`: true for the main administrator, otherwise the person's own rights, which is how a built-in
 * Administrátor whose role ticks *Vernostný program - Upraviť* stores the programme.
 *
 * The route itself is gated on the **permission** `loyalty_program`, which is the page key and is the owner's own
 * setting (owner question #184): an administrator whose role does not tick it does not reach this page at all.
 *
 * This is FB-001's resolution applied to a page rather than to a module: admit whoever the server lets read,
 * disable exactly what it will not let them store, and say which.
 *
 * `GET /api/v1/settings/loyalty` is `@RequireAuth({"main_admin", "admin"})` - measured 2026-08-11, a
 * request with no token answers **401**. It carried no guard at all when this note was first written
 * (FB-138), which is the sentence that used to be here; the customer app reads `/loyalty/public-info`
 * because that endpoint carries the Resend key and every mail and SMS template, not because it is open.
 */

/** The right the settings tab's writes ask - `BACKEND.md` §10A.413. */
export const LOYALTY_PROGRAM_EDIT = 'loyalty_program_edit'

export type LoyaltyTab = 'settings' | 'campaigns'

/**
 * Whether this person may store `tab`.
 *
 * **`hasPermission` is required** - §9.480's rule: an optional reader dropped at the call site would quietly put the
 * settings tab back on *main administrator only*, which is the defect this entry removes.
 */
export function mayWrite(
  tab: LoyaltyTab,
  role: string | undefined,
  hasPermission: (permission: string) => boolean,
): boolean {
  if (role === 'main_admin') return true
  if (tab === 'settings') return hasPermission(LOYALTY_PROGRAM_EDIT)
  // Every campaigns write is `@RequireAuth({"main_admin", "admin"})`.
  return role === 'admin'
}

/** Said on the tab, before anything is typed into a field that cannot be stored. */
export function readOnlyReason(
  tab: LoyaltyTab,
  role: string | undefined,
  hasPermission: (permission: string) => boolean,
): string | null {
  if (mayWrite(tab, role, hasPermission)) return null
  if (tab === 'settings') {
    // The right, under the roles screen's own label: what the person needs is a tick, not a person to ask.
    const right = `${tt('Nastavenia vernostného programu mení len ten, kto má právo')} „${tt('Vernostný program - Upraviť')}“.`
    // Said when it is true, so the refusal does not read as *you may do nothing here*.
    return mayWrite('campaigns', role, hasPermission)
      ? `${right} ${tt('Kampane na druhej záložke meniť môžete.')}`
      : right
  }
  return tt('Na zmenu vernostného programu nemáte právo. Údaje vidíte, uložiť ich nemôžete.')
}
